import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, ChevronDown, ChevronUp, Loader2, Plus, Search, Shield, Trophy } from "lucide-react";
import { FbmStatCell, FbmStatCellGraded } from "@/components/FbmStat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const MONTHS = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Header for a sortable column. */
function SortableTh({ label, sortKey, sortBy, sortDir, onClick, align = "right", testId }) {
  const active = sortBy === sortKey;
  return (
    <TableHead
      className={`px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] cursor-pointer select-none ${align === "right" ? "text-right" : ""}`}
      onClick={() => onClick(sortKey)}
      data-testid={testId}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sortDir === "asc"
            ? <ChevronUp className="h-3.5 w-3.5" data-testid={`${testId}-asc`} />
            : <ChevronDown className="h-3.5 w-3.5" data-testid={`${testId}-desc`} />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 opacity-25" />
        )}
      </span>
    </TableHead>
  );
}

/**
 * League drill-down page (Phase 8.2). Adds:
 *  - Correlation panel (r / r²) for position vs Overall above the table
 *  - FBM column (right of Defending), no color coding
 *  - Country column removed
 *  - Pos & Overall sortable headers with chevron indicator; nulls always last
 */
export default function LeagueDetailPage({ basePath = "/admin", canMutate }) {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = (user?.role === "admin");
  const canAdd = canMutate ?? isAdmin;
  const ignoreFriendlies = user?.ignore_friendlies ?? true;
  const [data, setData] = useState(null);
  const [leagueMeta, setLeagueMeta] = useState(null);  // for fbm_league_score (from /leagues list)
  const [loading, setLoading] = useState(true);
  // Default sort: Pos asc
  const [sortBy, setSortBy] = useState("pos");
  const [sortDir, setSortDir] = useState("asc");

  // Simplified "New Team" dialog (admin only, contextual to this league)
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPos, setNewPos] = useState("");
  const [savingNew, setSavingNew] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [d, ls] = await Promise.all([
        api.get(`/leagues/${id}/teams`),
        api.get(`/leagues`),
      ]);
      setData(d.data);
      setLeagueMeta((ls.data || []).find((l) => l.id === id) || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id, ignoreFriendlies]);

  const league = data?.league;
  const teams = data?.teams || [];
  const correlation = data?.correlation;
  const startsIn = league?.season_start_month;
  const fbmLeagueScore = leagueMeta?.fbm_league_score;
  const leagueCountry = league?.country || leagueMeta?.country || null;

  const onSubmitNewTeam = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) { toast.error("Name is required"); return; }
    const pit = newPos === "" ? null : parseInt(newPos, 10);
    if (pit != null && (Number.isNaN(pit) || pit < 1 || pit > 30)) {
      toast.error("Position must be between 1 and 30"); return;
    }
    setSavingNew(true);
    try {
      const payload = {
        name,
        country: leagueCountry || null,   // silently inherit; null OK if league has no country
        league_ids: [id],
        position_in_table: pit,
      };
      await api.post("/teams", payload);
      toast.success("Team created");
      setAddOpen(false);
      setNewName(""); setNewPos("");
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingNew(false); }
  };

  // Client-side sort — keeps the same fetched list, no extra API call.
  const sortedTeams = useMemo(() => {
    const out = [...teams];
    const keyFn = sortBy === "pos"
      ? (t) => t.position_in_table
      : (t) => t.fbm?.overall;
    const cmp = (a, b) => {
      const av = keyFn(a);
      const bv = keyFn(b);
      const aNull = av == null;
      const bNull = bv == null;
      // Nulls ALWAYS last, regardless of direction.
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      return sortDir === "asc" ? av - bv : bv - av;
    };
    out.sort(cmp);
    return out;
  }, [teams, sortBy, sortDir]);

  const onSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      // Position defaults asc (1, 2, 3); Overall defaults desc (best first).
      setSortDir(key === "pos" ? "asc" : "desc");
    }
  };

  const formatNumber = (v) => {
    if (v == null || Number.isNaN(v)) return "—";
    const rounded = Math.round(v * 100) / 100;
    // toFixed(2) ensures consistent "-0.83" / "0.69" rendering
    return rounded.toFixed(2);
  };

  return (
    <div data-testid="league-detail-page">
      <Link
        to={`${basePath}/leagues`}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground mb-6"
        data-testid="league-detail-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to leagues
      </Link>

      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2 inline-flex items-center gap-2">
            <Trophy className="h-3.5 w-3.5" /> League
          </div>
          <h1 className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight" data-testid="league-detail-name">
            {league?.name || (loading ? "Loading…" : "League")}
          </h1>
          {(leagueCountry || startsIn != null) && (
            <p className="text-muted-foreground mt-2 text-sm">
              {leagueCountry && (
                <span data-testid="league-detail-country" className="text-foreground/85 font-medium">{leagueCountry}</span>
              )}
              {leagueCountry && startsIn != null && <span className="mx-2">·</span>}
              {startsIn != null && (
                <>Season starts in <span className="text-foreground/85 font-medium" data-testid="league-detail-ssm">{MONTHS[startsIn] || startsIn}</span></>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {fbmLeagueScore != null && (
            <div
              className="px-4 py-2 rounded-lg border border-border bg-card text-right"
              data-testid="league-detail-fbm-badge"
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">FBM League Score</div>
              <div className="font-mono text-2xl tabular-nums leading-tight" data-testid="league-detail-fbm-score">
                {Math.ceil(Number(fbmLeagueScore))}
              </div>
            </div>
          )}
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground" data-testid="league-detail-count">
            {teams.length} {teams.length === 1 ? "team" : "teams"}
          </div>
          {canAdd && (
            <Button
              onClick={() => { setNewName(""); setNewPos(""); setAddOpen(true); }}
              className="h-11 rounded-none font-display uppercase tracking-[0.18em]"
              data-testid="league-detail-new-team-button"
            >
              <Plus className="h-4 w-4 mr-2" /> New Team
            </Button>
          )}
        </div>
      </header>

      {/* Correlation panel */}
      <section
        className="rounded-xl border border-border bg-card px-6 py-5 mb-6 flex flex-wrap items-center justify-between gap-4"
        data-testid="league-correlation-panel"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">
            Position vs Overall correlation
          </div>
          {!loading && correlation && correlation.n != null && correlation.n < 3 ? (
            <div className="text-sm text-muted-foreground" data-testid="league-correlation-empty">
              Not enough data to compute correlation.
            </div>
          ) : (
            <div className="flex items-baseline gap-6">
              <div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mr-2">|r|</span>
                <span className="font-mono text-2xl tabular-nums" data-testid="league-correlation-r">
                  {correlation?.r == null ? "—" : formatNumber(Math.abs(correlation.r))}
                </span>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mr-2">r²</span>
                <span className="font-mono text-2xl tabular-nums" data-testid="league-correlation-r2">
                  {formatNumber(correlation?.r_squared)}
                </span>
              </div>
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground max-w-xs">
          across {correlation?.n ?? 0} {correlation?.n === 1 ? "team" : "teams"} with both a position and an Overall score
        </p>
      </section>

      {/* Teams table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <Table data-testid="league-teams-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <SortableTh
                label="Pos" sortKey="pos" sortBy={sortBy} sortDir={sortDir}
                onClick={onSort} align="left" testId="sort-pos"
              />
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Name</TableHead>
              <SortableTh
                label="Overall" sortKey="overall" sortBy={sortBy} sortDir={sortDir}
                onClick={onSort} testId="sort-overall"
              />
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Finishing</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Passing</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Defending</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">FBM</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="px-6 py-12 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : sortedTeams.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="px-6 py-16 text-center">
                <Shield className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                <div className="font-display uppercase tracking-wider">No teams yet in this league</div>
              </TableCell></TableRow>
            ) : (
              sortedTeams.map((t) => (
                <TableRow
                  key={t.id}
                  className="tr-fade border-border hover:bg-muted/30 cursor-pointer"
                  data-testid={`league-team-row-${t.id}`}
                >
                  <TableCell className="px-6 py-4 text-left font-mono text-sm">
                    <Link to={`${basePath}/teams/${t.id}`} className="block w-full" data-testid={`league-team-pos-${t.id}`}>
                      {t.position_in_table == null ? <span className="text-muted-foreground">—</span> : t.position_in_table}
                    </Link>
                  </TableCell>
                  <TableCell className="px-6 py-4 font-medium">
                    <Link to={`${basePath}/teams/${t.id}`} className="hover:underline block" data-testid={`league-team-link-${t.id}`}>
                      {t.name}
                    </Link>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <FbmStatCellGraded value={t.fbm?.overall} testId={`league-team-overall-${t.id}`} />
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <FbmStatCellGraded value={t.fbm?.finishing} testId={`league-team-finishing-${t.id}`} />
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <FbmStatCellGraded value={t.fbm?.passing} testId={`league-team-passing-${t.id}`} />
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <FbmStatCellGraded value={t.fbm?.defending} testId={`league-team-defending-${t.id}`} />
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right font-mono text-sm" data-testid={`league-team-fbm-${t.id}`}>
                    {t.fbm?.fbm_score == null
                      ? <span className="text-muted-foreground">—</span>
                      : Math.ceil(Number(t.fbm.fbm_score))}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Contextual "New Team" dialog — admin only. Name + Position only.
          Country/league are inherited silently from the parent league. */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="league-new-team-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">New Team</DialogTitle>
            <DialogDescription>
              Adds a team to <span className="text-foreground/85 font-medium">{league?.name || "this league"}</span>
              {leagueCountry ? <> · <span className="text-foreground/85">{leagueCountry}</span></> : null}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmitNewTeam} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="league-new-team-name">Name</Label>
              <Input
                id="league-new-team-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                required
                autoFocus
                data-testid="league-new-team-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="league-new-team-pos">Position in table (optional)</Label>
              <Input
                id="league-new-team-pos"
                type="number" min={1} max={30}
                value={newPos}
                onChange={(e) => setNewPos(e.target.value)}
                placeholder="1–30"
                data-testid="league-new-team-pos-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={savingNew} data-testid="league-new-team-save-button">
                {savingNew ? "Saving…" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
